import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Integration, Integration_Types, CreateIntegrationDto, UpdateIntegrationDto } from 'app/models/integration.models';

export interface IntegrationDialogData {
  integration?: Integration;
  mode: 'create' | 'edit';
}

@Component({
  selector: 'app-integration-dialog',
  templateUrl: './integration-dialog.component.html',
  styleUrls: ['./integration-dialog.component.css']
})
export class IntegrationDialogComponent implements OnInit {
  
  integrationForm: FormGroup;
  mode: 'create' | 'edit';
  
  // Tipos de integración disponibles
  integrationTypes = [
    { value: Integration_Types.PAYMENT_GATEWAY, label: 'Método de Pago' },
    { value: Integration_Types.SHIPPING, label: 'Envío' },
    { value: Integration_Types.INVENTORY, label: 'Inventario' },
    { value: Integration_Types.ACCOUNTING, label: 'Contabilidad' },
    { value: Integration_Types.CRM, label: 'CRM' },
    { value: Integration_Types.EMAIL, label: 'Email' },
    { value: Integration_Types.SMS, label: 'SMS' }
  ];

  // Mostrar/ocultar token
  showToken = false;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<IntegrationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IntegrationDialogData
  ) {
    this.mode = data.mode;
    
    // Inicializar formulario
    this.integrationForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      type: [Integration_Types.PAYMENT_GATEWAY, [Validators.required]],
      apiUrl: ['', [Validators.required, Validators.maxLength(255)]],
      token: ['', [Validators.required, Validators.maxLength(500)]],
      isActive: [true],
      description: ['', [Validators.maxLength(500)]]
    });
  }

  ngOnInit(): void {
    // Si es modo edición, cargar datos existentes
    if (this.mode === 'edit' && this.data.integration) {
      this.integrationForm.patchValue({
        name: this.data.integration.name,
        type: this.data.integration.type,
        apiUrl: this.data.integration.apiUrl,
        token: this.data.integration.token,
        isActive: this.data.integration.isActive ?? true,
        description: this.data.integration.description || ''
      });
    }
  }

  /**
   * Obtiene el título del diálogo
   */
  getTitle(): string {
    return this.mode === 'create' ? 'Agregar Integración' : 'Editar Integración';
  }

  /**
   * Alterna la visibilidad del token
   */
  toggleTokenVisibility(): void {
    this.showToken = !this.showToken;
  }

  /**
   * Guarda la integración
   */
  onSave(): void {
    if (this.integrationForm.valid) {
      const formValue = this.integrationForm.value;
      
      if (this.mode === 'create') {
        const createDto: CreateIntegrationDto = {
          name: formValue.name.trim(),
          type: formValue.type,
          apiUrl: formValue.apiUrl.trim(),
          token: formValue.token.trim(),
          isActive: formValue.isActive,
          description: formValue.description?.trim() || undefined
        };
        this.dialogRef.close(createDto);
      } else {
        const updateDto: UpdateIntegrationDto = {
          name: formValue.name.trim(),
          type: formValue.type,
          apiUrl: formValue.apiUrl.trim(),
          token: formValue.token.trim(),
          isActive: formValue.isActive,
          description: formValue.description?.trim() || undefined
        };
        this.dialogRef.close(updateDto);
      }
    }
  }

  /**
   * Cancela y cierra el diálogo
   */
  onCancel(): void {
    this.dialogRef.close();
  }

  /**
   * Verifica si un campo tiene errores
   */
  hasError(field: string, error: string): boolean {
    const control = this.integrationForm.get(field);
    return !!(control && control.hasError(error) && (control.dirty || control.touched));
  }
}
